/**
 * Environment Variables
 * Centralized environment variable access with type safety
 */
const getEnvVar = (key: string, fallback = ''): string => {
    const value = process.env[key];
    if (!value && !fallback) {
        console.warn(`Environment variable ${key} is not defined`);
    }
    return value || fallback;
};

export const ENV = {
    CORE_API: getEnvVar('REACT_APP_ACP_CORE_API'),
    CODE_API: getEnvVar('REACT_APP_ACP_CODE_API'),
    VSCODE: getEnvVar('REACT_APP_ACP_VSCODE_API'),
    GEMINI_API_KEY: getEnvVar('REACT_APP_ACP_GEMINI_API_KEY'),
} as const;

// Legacy exports for backward compatibility
export const CORE_API = ENV.CORE_API;
export const CODE_API = ENV.CODE_API;
export const VSCODE = ENV.VSCODE;
export const GEMINI_API_KEY = ENV.GEMINI_API_KEY;

/**
 * Application Routes
 * Define all route paths as constants for type-safe routing
 */
export const ROUTES = {
    HOME: '/',
    LOGIN: '/login',
    ABOUT: '/about',
    CHALLENGES: '/challenges',
    WELCOME_CHALLENGE: '/challenges/welcome',
    SUBMITTED_CHALLENGE: '/challenges/submited',
    SUBMITTED_CONTEST: '/contests/submited',
    CHALLENGES_LANGUAGE: '/challenges-language',
    RESULT: '/result',
    EDITOR: '/editor',
    LANGUAGES: '/languages',
    CASES: '/cases',
    USERS: '/users',
    CANDIDATES: '/candidates',
    CANDIDATE_EXPERIENCE: '/candidate-experience',
    INTELLIGENCE: '/intelligence',
    INTERVIEWS: '/interviews',
    TRAINING: '/trainings',
    TIME_CAPTURE: '/time-capture',
    INFO: '/info',
    SUBMITTED: '/submitted',
    CONTESTS: '/contests',
    CONTEST_DETAIL: '/contests/:id',
    HACKATHONS: '/hackathons',
    HACKATHON_DETAIL: '/hackathons/:id',
    HACKATHON_SUBMISSIONS: '/hackathons/:id/submissions',
    HACKATHON_RESULTS: '/hackathons/:id/results',
    SUBMIT_PROJECT: '/submit-project',
    HACKATHON_REVIEW: '/hackathon-review',
    CONTEST_SUBMISSIONS: '/contests/:id/submissions',
    CONTEST_RESULTS: '/contests/:id/results',
    CONTEST_RESULT_DETAIL: '/contests/result',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RouteValue = typeof ROUTES[RouteKey];

/**
 * User Roles
 * Define all user roles in the application
 */
export const ROLES = {
    ADMIN: 'Admin',
    TA: 'TA',
    INTERVIEWER: 'Interviewer',
    CANDIDATE: 'Candidate',
    USER: 'User',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/**
 * Theme Modes
 * Light and dark mode constants
 */
export const LIGHT_MODES = {
    LIGHT: 'light',
    DARK: 'dark',
} as const;

export type LightMode = typeof LIGHT_MODES[keyof typeof LIGHT_MODES];

/**
 * Navigation Configuration
 * Define navigation pages for different user roles
 */
export const NAVBAR_PAGES = {
    ADMIN: ['Home', 'Trainings', 'Interviews', 'Contests', 'Hackathons', 'Languages', 'Users', 'About'] as const,
    TA: ['Home', 'Interviews', 'About'] as const,
    INTERVIEWER: ['Home', 'Trainings', 'Interviews', 'About'] as const,
    USER: ['Home', 'Trainings', 'About'] as const,
} as const;

// Legacy exports for backward compatibility
export const NAVBAR_ADMIN_HEADER_PAGES = NAVBAR_PAGES.ADMIN;
export const NAVBAR_TA_HEADER_PAGES = NAVBAR_PAGES.TA;
export const NAVBAR_INTERVIEWER_HEADER_PAGES = NAVBAR_PAGES.INTERVIEWER;
export const NAVBAR_USER_HEADER_PAGES = NAVBAR_PAGES.USER; 

/**
 * HTTP Methods
 * Standard HTTP methods used in API calls
 */
export const API_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
} as const;

export type ApiMethod = typeof API_METHODS[keyof typeof API_METHODS];

/**
 * API Routes
 * Define all API endpoints as constants
 */
export const API_ROUTES = {
    USER: '/api/v1/user',
    CANDIDATE: '/api/v1/candidate',
    CHALLENGES_DATA: '/api/v1/workspacechallengeinfo',
    ACCESS_LOGIN: '/api/v1/access/login',
    INTERVIEW_LOGIN: '/api/v1/access/interview',
    GUEST_OTP_REQUEST: '/api/v1/access/guest',
    GUEST_OTP_VERIFY: '/api/v1/access/guest/verify',
    INTERVIEW_CLOSE: '/api/v1/interview/{id}/submit',
    INTERVIEW_PREPARE: '/api/v1/interview/{id}/prepare',
    INTERVIEW: '/api/v1/interview',
    CHALLENGES: '/api/v1/challenge',
    INTERVIEW_BY_USER: '/api/v1/interview/user/{id}',
    INTERVIEW_RESULTS: '/api/v1/interview/token/{token}',
    CHALLENGES_CATALOG: '/api/v1/challengesbylevel',
    CODE_SOURCES: '/api/v1/codesources',
    LANGUAGE: '/api/v1/language',
    CONTEST: '/api/v1/contest',
    CONTEST_DETAIL: '/api/v1/contest/{id}',
    HACKATHON: '/api/v1/hackathons',
    HACKATHON_DETAIL: '/api/v1/hackathons/{id}',
    HACKATHON_TEAMS: '/api/v1/teams/hackathon/{id}',
    TEAMS: '/api/v1/teams',
    TEAM_DETAIL: '/api/v1/teams/{id}',
    TEAM_MEMBERS: '/api/v1/teams/members',
    TEAM_MEMBERS_DETAIL: '/api/v1/teams/{id}/members',
    SUBMISSIONS: '/api/v1/submissions',
    SUBMISSION_DETAIL: '/api/v1/submissions/{id}',
    SUBMISSION_UPLOAD: '/api/v1/submissions/{id}/upload',
    SUBMISSION_SCORE: '/api/v1/submissions/{id}/score',
    SUBMISSION_SCORES: '/api/v1/submissions/{id}/scores',
    SUBMISSION_FEEDBACK: '/api/v1/submissions/{id}/feedback',
    AI_HACKATHON_SCORE: '/api/v1/evaluate-hackathon',
    FILE_UPLOAD: '/api/v1/files/upload',
    CONTEST_SUBMIT: '/api/v1/contest/{id}/submit',
    CONTEST_RESULTS: '/api/v1/contest/{id}/results',
    PROMPT: '/api/ai/prompt'
} as const;

/**
 * Helper function to replace route parameters
 * @param route - Route template with placeholders
 * @param params - Object containing parameter values
 * @returns Formatted route with parameters replaced
 */
export const formatRoute = (
    route: string,
    params: Record<string, string | number>
): string => {
    let formattedRoute = route;
    Object.entries(params).forEach(([key, value]) => {
        formattedRoute = formattedRoute.replace(`{${key}}`, String(value));
    });
    return formattedRoute;
};

/**
 * Styling Constants
 * Reusable style classes
 */
export const STYLES = {
    DEFAULT_CELL_TYPOGRAPHY:
        'font-roboto text-base font-normal leading-5 tracking-wide text-left decoration-skip-ink',
} as const;

// Legacy export for backward compatibility
export const DEFAULT_STYLE_TYPOGRAPHY_CELL = STYLES.DEFAULT_CELL_TYPOGRAPHY;